import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Button } from '@/Components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Badge } from '@/Components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Components/ui/tabs';
import { Skeleton } from '@/Components/ui/skeleton';
import { Label } from '@/Components/ui/label';
import { Input } from '@/Components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select';
import { Textarea } from '@/Components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/Components/ui/dialog';
import { useQueryClient } from '@tanstack/react-query';
import {
    Zap,
    Wrench,
    FileText,
    CheckCircle,
    LogOut,
    User,
    ChevronRight,
    Download,
    Calendar,
    Shield,
    Loader2,
    Plus,
    Bell,
} from 'lucide-react';
import RepairTimeline from '../Components/cliente/RepairTimeline.jsx';
import SEO from '../Components/SEO.jsx';


const statusColors = {
    ricevuto: 'bg-blue-100 text-blue-700',
    diagnosi: 'bg-yellow-100 text-yellow-700',
    riparazione: 'bg-orange-100 text-orange-700',
    attesa_ricambi: 'bg-purple-100 text-purple-700',
    completato: 'bg-green-100 text-green-700',
    consegnato: 'bg-emerald-100 text-emerald-700'
};

const statusLabels = {
    ricevuto: 'Ricevuto',
    diagnosi: 'In Diagnosi',
    riparazione: 'In Riparazione',
    attesa_ricambi: 'Attesa Ricambi',
    completato: 'Completato',
    consegnato: 'Consegnato'
};

export default function AreaClienti() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('ups');
    const [selectedRiparazione, setSelectedRiparazione] = useState(null);
    const [selectedUPS, setSelectedUPS] = useState(null);
    const [showAddUPS, setShowAddUPS] = useState(false);
    const [upsFormData, setUpsFormData] = useState({
        marca: '',
        modello: '',
        potenza_va: '',
        matricola: '',
        ubicazione: '',
        note: ''
    });
    const [submittingUPS, setSubmittingUPS] = useState(false);
    const queryClient = useQueryClient();

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const authenticated = await base44.auth.isAuthenticated();
            if (authenticated) {
                const userData = await base44.auth.me();
                setUser(userData);
                setIsAuthenticated(true);

                if (userData.role === 'admin') {
                    window.location.href = '/admin';
                    return;
                }
            }
        } catch (error) {
            console.log('Not authenticated');
        }
        setLoading(false);
    };

    const handleLogin = () => {
        base44.auth.redirectToLogin(window.location.href);
    };

    const handleLogout = () => {
        base44.auth.logout();
    };

    // Fetch user's UPS
    const { data: myUPS = [], isLoading: loadingUPS } = useQuery({
        queryKey: ['myUPS', user?.email],
        queryFn: () => base44.entities.UPS.filter({ cliente_email: user?.email }),
        enabled: !!user?.email
    });

    // Fetch user's repairs
    const { data: myRiparazioni = [], isLoading: loadingRiparazioni } = useQuery({
        queryKey: ['myRiparazioni', user?.email],
        queryFn: () => base44.entities.Riparazione.filter({ cliente_email: user?.email }, '-created_at'),
        enabled: !!user?.email
    });

    // Fetch repair states for selected repair
    const { data: statiRiparazione = [] } = useQuery({
        queryKey: ['statiRiparazione', selectedRiparazione?.id],
        queryFn: () => base44.entities.StatoRiparazione.filter({ riparazione_id: selectedRiparazione?.id }, 'created_at'),
        enabled: !!selectedRiparazione?.id
    });

    // Fetch user's documents
    const { data: myDocumenti = [], isLoading: loadingDocumenti } = useQuery({
        queryKey: ['myDocumenti', user?.email],
        queryFn: () => base44.entities.Documento.filter({ cliente_email: user?.email }, '-created_at'),
        enabled: !!user?.email
    });

    // Fetch user's contracts
    const { data: myContratti = [], isLoading: loadingContratti } = useQuery({
        queryKey: ['myContratti', user?.email],
        queryFn: () => base44.entities.ContrattoManutenzione.filter({ cliente_email: user?.email }),
        enabled: !!user?.email
    });

    // Fetch user's notifications with real-time updates
    const { data: myNotifiche = [], refetch: refetchNotifiche } = useQuery({
        queryKey: ['myNotifiche', user?.email],
        queryFn: () => base44.entities.Notifica.filter({ cliente_email: user?.email, letta: false }, '-created_at', 5),
        enabled: !!user?.email
    });

    // Real-time subscription for notifications
    useEffect(() => {
        if (!user?.email) return;

        const unsubscribe = base44.entities.Notifica.subscribe((event) => {
            if (event.type === 'create' && event.data.cliente_email === user.email) {
                refetchNotifiche();
            }
        });

        return () => unsubscribe();
    }, [user?.email, refetchNotifiche]);

    // Real-time subscription for repair updates
    useEffect(() => {
        if (!user?.email) return;

        const unsubscribe = base44.entities.Riparazione.subscribe((event) => {
            if (event.type === 'update') {
                // Refetch repairs if they belong to this user
                queryClient.invalidateQueries({ queryKey: ['myRiparazioni'] });
            }
        });

        return () => unsubscribe();
    }, [user?.email, queryClient]);


    const handleAddUPS = async (e) => {
        e.preventDefault();
        setSubmittingUPS(true);

        try {
            await base44.entities.UPS.create({
                ...upsFormData,
                cliente_email: user.email,
                data_installazione: new Date().toISOString().split('T')[0],
                potenza_va: upsFormData.potenza_va ? parseInt(upsFormData.potenza_va) : null
            });

            await base44.entities.Notifica.create({
                cliente_email: user.email,
                tipo: 'ups_registrato',
                titolo: 'UPS Registrato',
                messaggio: `Hai registrato un nuovo UPS: ${upsFormData.marca} ${upsFormData.modello}`
            });

            setShowAddUPS(false);
            setUpsFormData({ marca: '', modello: '', potenza_va: '', matricola: '', ubicazione: '', note: '' });
            alert('UPS registrato con successo!');
        } catch (error) {
            console.error('Errore durante la registrazione:', error);
            alert(`Errore: ${error.message || 'Impossibile registrare UPS'}`);
        } finally {
            setSubmittingUPS(false);
        }
    };

    const handleMarkNotificationRead = async (notifica) => {
        await base44.entities.Notifica.update(notifica.id, { letta: true });
        refetchNotifiche();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 pt-32 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-50">
                {/* Hero */}
                <section className="relative bg-gray-900 pt-32 pb-32">
                    <div className="absolute inset-0">
                        <img
                            src="https://images.unsplash.com/photo-1625314887424-9f190599bd56?w=1920&q=80"
                            alt="UPS Systems"
                            className="w-full h-full object-cover opacity-20"
                        />
                    </div>
                    <div className="relative max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-blue-600 flex items-center justify-center">
                                <User className="w-10 h-10 text-white" />
                            </div>
                            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                                Area Clienti
                            </h1>
                            <p className="text-blue-400 font-medium mb-4 uppercase tracking-wider">Coming Soon</p>
                            <p className="text-lg text-gray-300 mb-8">
                                Accedi per monitorare i tuoi UPS, le riparazioni in corso e scaricare i documenti.
                            </p>
                            <Button
                                onClick={handleLogin}
                                size="lg"
                                className="rounded-full bg-blue-600 hover:bg-blue-700 px-8 text-white"
                            >
                                Accedi o Registrati
                            </Button>
                        </motion.div>
                    </div>
                </section>

                {/* Features */}
                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid md:grid-cols-3 gap-8">
                            {[
                                {
                                    icon: Zap,
                                    title: 'I Tuoi UPS',
                                    description: 'Visualizza tutti i gruppi di continuità registrati con dettagli tecnici'
                                },
                                {
                                    icon: Wrench,
                                    title: 'Tracking Riparazioni',
                                    description: 'Segui in tempo reale lo stato delle riparazioni con timeline dettagliata'
                                },
                                {
                                    icon: FileText,
                                    title: 'Documenti',
                                    description: 'Scarica preventivi, fatture e report tecnici in qualsiasi momento'
                                }
                            ].map((feature, index) => (
                                <motion.div
                                    key={feature.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <Card className="h-full text-center hover:shadow-lg transition-shadow">
                                        <CardContent className="pt-8">
                                            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-blue-100 flex items-center justify-center">
                                                <feature.icon className="w-7 h-7 text-blue-600" />
                                            </div>
                                            <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                                            <p className="text-sm text-gray-600">{feature.description}</p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // Authenticated view
    return (
        <div className="min-h-screen bg-gray-50 pt-28 pb-12">
            <SEO
                title="Area Clienti - Dashboard Personale | Tecnosys"
                description="Accedi alla tua area riservata per monitorare UPS, riparazioni e contratti."
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            Benvenuto, {user?.full_name || 'Cliente'}
                        </h1>
                        <p className="text-gray-600">{user?.email}</p>
                    </div>
                    <Button variant="outline" onClick={handleLogout} className="gap-2">
                        <LogOut className="w-4 h-4" />
                        Esci
                    </Button>
                </div>

                {/* Notifications Banner */}
                {myNotifiche.length > 0 && (
                    <Card className="mb-6 border-blue-200 bg-blue-50">
                        <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                                <Bell className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                                <div className="flex-1 space-y-2">
                                    {myNotifiche.slice(0, 3).map((notifica) => (
                                        <div key={notifica.id} className="flex items-start justify-between gap-4">
                                            <div>
                                                <p className="font-medium text-sm text-gray-900">{notifica.titolo}</p>
                                                <p className="text-sm text-gray-600">{notifica.messaggio}</p>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleMarkNotificationRead(notifica)}
                                            >
                                                ✓
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Stats Cards */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {[
                        {
                            label: 'I Miei UPS',
                            value: myUPS.length,
                            icon: Zap,
                            color: 'bg-blue-500',
                            tab: 'ups'
                        },
                        {
                            label: 'Riparazioni Attive',
                            value: myRiparazioni.filter(r => r.stato_corrente !== 'consegnato').length,
                            icon: Wrench,
                            color: 'bg-orange-500',
                            tab: 'riparazioni'
                        },
                        {
                            label: 'Documenti',
                            value: myDocumenti.length,
                            icon: FileText,
                            color: 'bg-green-500',
                            tab: 'documenti'
                        },
                        {
                            label: 'Contratti Attivi',
                            value: myContratti.filter(c => c.stato === 'attivo').length,
                            icon: Shield,
                            color: 'bg-purple-500',
                            tab: 'contratti'
                        }
                    ].map((stat) => (
                        <Card
                            key={stat.label}
                            className="cursor-pointer hover:shadow-md transition-shadow"
                            onClick={() => setActiveTab(stat.tab)}
                        >
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                                    <stat.icon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                                    <p className="text-sm text-gray-500">{stat.label}</p>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Main Content */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="!bg-white border w-full !h-auto flex flex-wrap justify-center sm:justify-start gap-1 p-1">
                        <TabsTrigger value="ups" className="gap-2">
                            <Zap className="w-4 h-4" />
                            I Miei UPS
                        </TabsTrigger>
                        <TabsTrigger value="riparazioni" className="gap-2">
                            <Wrench className="w-4 h-4" />
                            Riparazioni
                        </TabsTrigger>
                        <TabsTrigger value="documenti" className="gap-2">
                            <FileText className="w-4 h-4" />
                            Documenti
                        </TabsTrigger>
                        <TabsTrigger value="contratti" className="gap-2">
                            <Shield className="w-4 h-4" />
                            Contratti
                        </TabsTrigger>
                    </TabsList>

                    {/* Riparazioni Tab */}
                    <TabsContent value="riparazioni" className="space-y-6">
                        {loadingRiparazioni ? (
                            <div className="space-y-4">
                                {[1, 2].map(i => (
                                    <Skeleton key={i} className="h-32 w-full" />
                                ))}
                            </div>
                        ) : myRiparazioni.length > 0 ? (
                            <div className="grid lg:grid-cols-2 gap-6">
                                {/* Lista riparazioni */}
                                <div className="space-y-4">
                                    <h3 className="font-semibold text-gray-900">Le tue riparazioni</h3>
                                    {myRiparazioni.map((riparazione) => (
                                        <motion.div
                                            key={riparazione.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                        >
                                            <Card
                                                className={`cursor-pointer transition-all hover:shadow-md ${selectedRiparazione?.id === riparazione.id ? 'ring-2 ring-blue-500' : ''
                                                    }`}
                                                onClick={() => setSelectedRiparazione(riparazione)}
                                            >
                                                <CardContent className="p-4">
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <p className="font-semibold text-gray-900">
                                                                Ticket #{riparazione.id?.slice(-6).toUpperCase()}
                                                            </p>
                                                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                                                {riparazione.descrizione_problema}
                                                            </p>
                                                            <p className="text-xs text-gray-400 mt-2">
                                                                Aperto il {format(new Date(riparazione.created_at), 'dd MMM yyyy', { locale: it })}
                                                            </p>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-2">
                                                            <Badge className={statusColors[riparazione.stato_corrente]}>
                                                                {statusLabels[riparazione.stato_corrente]}
                                                            </Badge>
                                                            <ChevronRight className="w-5 h-5 text-gray-400" />
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* Dettaglio riparazione */}
                                <div>
                                    <AnimatePresence mode="wait">
                                        {selectedRiparazione ? (
                                            <motion.div
                                                key={selectedRiparazione.id}
                                                initial={{ opacity: 0, x: 20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: -20 }}
                                            >
                                                <Card>
                                                    <CardHeader>
                                                        <CardTitle className="flex items-center justify-between">
                                                            <span>Ticket #{selectedRiparazione.id?.slice(-6).toUpperCase()}</span>
                                                            <Badge className={statusColors[selectedRiparazione.stato_corrente]}>
                                                                {statusLabels[selectedRiparazione.stato_corrente]}
                                                            </Badge>
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardContent className="space-y-6">
                                                        <RepairTimeline
                                                            currentStatus={selectedRiparazione.stato_corrente}
                                                            statiRiparazione={statiRiparazione}
                                                        />

                                                        <div className="pt-4 border-t space-y-3">
                                                            <div>
                                                                <p className="text-sm text-gray-500">Problema segnalato</p>
                                                                <p className="text-gray-900">{selectedRiparazione.descrizione_problema}</p>
                                                            </div>
                                                            {selectedRiparazione.note_tecniche && (
                                                                <div>
                                                                    <p className="text-sm text-gray-500">Note tecniche</p>
                                                                    <p className="text-gray-900">{selectedRiparazione.note_tecniche}</p>
                                                                </div>
                                                            )}
                                                            {selectedRiparazione.costo_preventivato && (
                                                                <div>
                                                                    <p className="text-sm text-gray-500">Costo preventivato</p>
                                                                    <p className="text-lg font-semibold text-gray-900">
                                                                        € {selectedRiparazione.costo_preventivato.toFixed(2)}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </motion.div>
                                        ) : (
                                            <Card className="h-64 flex items-center justify-center">
                                                <div className="text-center text-gray-500">
                                                    <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                                    <p>Seleziona una riparazione per vedere i dettagli</p>
                                                </div>
                                            </Card>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        ) : (
                            <Card className="p-8 text-center">
                                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                                <h3 className="font-semibold text-gray-900 mb-2">Nessuna riparazione attiva</h3>
                                <p className="text-gray-600 mb-4">Tutti i tuoi UPS sono in perfetto stato!</p>
                                <Link to={createPageUrl('Riparazioni')}>
                                    <Button variant="outline" className="rounded-full">
                                        Segnala un guasto
                                    </Button>
                                </Link>
                            </Card>
                        )}
                    </TabsContent>

                    {/* UPS Tab */}
                    <TabsContent value="ups">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-semibold text-gray-900">I Miei UPS</h3>
                            <Button
                                onClick={() => setShowAddUPS(!showAddUPS)}
                                className="rounded-full bg-blue-600 hover:bg-blue-700 gap-2 text-white"
                            >
                                <Plus className="w-4 h-4" />
                                Registra Nuovo UPS
                            </Button>
                        </div>

                        {showAddUPS && (
                            <Card className="mb-6">
                                <CardContent className="p-6">
                                    <h4 className="font-semibold text-gray-900 mb-4">Registra un nuovo UPS</h4>
                                    <form onSubmit={handleAddUPS} className="space-y-4">
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div>
                                                <Label>Marca *</Label>
                                                <Select
                                                    value={upsFormData.marca}
                                                    onValueChange={(value) => setUpsFormData({ ...upsFormData, marca: value })}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Seleziona marca" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {['Riello', 'AEC', 'APC', 'Eaton', 'Vertiv', 'Altro'].map(m => (
                                                            <SelectItem key={m} value={m}>{m}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div>
                                                <Label>Modello *</Label>
                                                <Input
                                                    value={upsFormData.modello}
                                                    onChange={(e) => setUpsFormData({ ...upsFormData, modello: e.target.value })}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div>
                                                <Label>Potenza (VA)</Label>
                                                <Input
                                                    type="number"
                                                    value={upsFormData.potenza_va}
                                                    onChange={(e) => setUpsFormData({ ...upsFormData, potenza_va: e.target.value })}
                                                    placeholder="Es. 3000"
                                                />
                                            </div>
                                            <div>
                                                <Label>Matricola</Label>
                                                <Input
                                                    value={upsFormData.matricola}
                                                    onChange={(e) => setUpsFormData({ ...upsFormData, matricola: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <Label>Ubicazione</Label>
                                            <Input
                                                value={upsFormData.ubicazione}
                                                onChange={(e) => setUpsFormData({ ...upsFormData, ubicazione: e.target.value })}
                                                placeholder="Es. Sala server, Piano 2"
                                            />
                                        </div>
                                        <div>
                                            <Label>Note</Label>
                                            <Textarea
                                                value={upsFormData.note}
                                                onChange={(e) => setUpsFormData({ ...upsFormData, note: e.target.value })}
                                                rows={2}
                                            />
                                        </div>
                                        <div className="flex gap-3">
                                            <Button type="submit" disabled={submittingUPS} className="rounded-full">
                                                {submittingUPS ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Registra UPS'}
                                            </Button>
                                            <Button type="button" variant="outline" onClick={() => setShowAddUPS(false)} className="rounded-full">
                                                Annulla
                                            </Button>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                        )}

                        {loadingUPS ? (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {[1, 2, 3].map(i => (
                                    <Skeleton key={i} className="h-48" />
                                ))}
                            </div>
                        ) : myUPS.length > 0 ? (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {myUPS.map((ups) => (
                                    <Card
                                        key={ups.id}
                                        className="hover:shadow-md transition-shadow cursor-pointer"
                                        onClick={() => setSelectedUPS(ups)}
                                    >
                                        <CardContent className="p-6">
                                            <div className="flex items-start gap-4">
                                                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                                                    <Zap className="w-6 h-6 text-blue-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-semibold text-gray-900">{ups.marca} {ups.modello}</p>
                                                    <p className="text-sm text-gray-600">{ups.potenza_va?.toLocaleString()} VA</p>
                                                    {ups.matricola && (
                                                        <p className="text-xs text-gray-400 mt-1">S/N: {ups.matricola}</p>
                                                    )}
                                                    {ups.ubicazione && (
                                                        <p className="text-xs text-gray-500 mt-2">{ups.ubicazione}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <Card className="p-8 text-center">
                                <Zap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                <h3 className="font-semibold text-gray-900 mb-2">Nessun UPS registrato</h3>
                                <p className="text-gray-600">I tuoi UPS verranno visualizzati qui dopo il primo intervento.</p>
                            </Card>
                        )}
                    </TabsContent>

                    {/* Documenti Tab */}
                    <TabsContent value="documenti">
                        {loadingDocumenti ? (
                            <div className="space-y-3">
                                {[1, 2, 3].map(i => (
                                    <Skeleton key={i} className="h-16" />
                                ))}
                            </div>
                        ) : myDocumenti.length > 0 ? (
                            <div className="space-y-3">
                                {myDocumenti.map((doc) => (
                                    <Card key={doc.id}>
                                        <CardContent className="p-4 flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                                    <FileText className="w-5 h-5 text-gray-600" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900">{doc.nome_file}</p>
                                                    <p className="text-sm text-gray-500">
                                                        {doc.tipo?.replace('_', ' ')} • {format(new Date(doc.created_at), 'dd MMM yyyy', { locale: it })}
                                                    </p>
                                                </div>
                                            </div>
                                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                                <Button variant="ghost" size="icon">
                                                    <Download className="w-5 h-5" />
                                                </Button>
                                            </a>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <Card className="p-8 text-center">
                                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                <h3 className="font-semibold text-gray-900 mb-2">Nessun documento</h3>
                                <p className="text-gray-600">I tuoi documenti appariranno qui.</p>
                            </Card>
                        )}
                    </TabsContent>

                    {/* Contratti Tab */}
                    <TabsContent value="contratti">
                        {loadingContratti ? (
                            <div className="space-y-4">
                                {[1].map(i => (
                                    <Skeleton key={i} className="h-40" />
                                ))}
                            </div>
                        ) : myContratti.length > 0 ? (
                            <div className="space-y-4">
                                {myContratti.map((contratto) => (
                                    <Card key={contratto.id}>
                                        <CardContent className="p-6">
                                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                                <div>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Shield className="w-5 h-5 text-blue-600" />
                                                        <h3 className="font-semibold text-gray-900">
                                                            Piano {contratto.piano?.charAt(0).toUpperCase() + contratto.piano?.slice(1)}
                                                        </h3>
                                                        <Badge className={contratto.stato === 'attivo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                                                            {contratto.stato?.charAt(0).toUpperCase() + contratto.stato?.slice(1)}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-gray-600">
                                                        <span className="flex items-center gap-1">
                                                            <Calendar className="w-4 h-4" />
                                                            Scade: {format(new Date(contratto.data_scadenza), 'dd MMM yyyy', { locale: it })}
                                                        </span>
                                                        <span>
                                                            Visite: {contratto.visite_effettuate || 0}/{contratto.visite_previste || 0}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    {contratto.importo_annuo && (
                                                        <p className="text-lg font-semibold text-gray-900">
                                                            € {contratto.importo_annuo.toFixed(2)}/anno
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <Card className="p-8 text-center">
                                <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                <h3 className="font-semibold text-gray-900 mb-2">Nessun contratto attivo</h3>
                                <p className="text-gray-600 mb-4">Proteggi i tuoi UPS con un piano di manutenzione.</p>
                                <Link to={createPageUrl('Manutenzione')}>
                                    <Button className="rounded-full bg-blue-600 hover:bg-blue-700 text-white">
                                        Scopri i piani
                                    </Button>
                                </Link>
                            </Card>
                        )}
                    </TabsContent>

                    {/* FAQ Tab */}
                </Tabs>

                {/* UPS Detail Modal */}
                <Dialog open={!!selectedUPS} onOpenChange={() => setSelectedUPS(null)}>
                    <DialogContent className="sm:max-w-md bg-white">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Zap className="w-5 h-5 text-blue-600" />
                                Dettagli UPS
                            </DialogTitle>
                            <DialogDescription>
                                Informazioni complete sul gruppo di continuità selezionato.
                            </DialogDescription>
                        </DialogHeader>
                        {selectedUPS && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Marca</p>
                                        <p className="text-gray-900 font-semibold">{selectedUPS.marca}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Modello</p>
                                        <p className="text-gray-900 font-semibold">{selectedUPS.modello}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Potenza</p>
                                        <p className="text-gray-900">{selectedUPS.potenza_va?.toLocaleString()} VA</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Matricola</p>
                                        <p className="text-gray-900">{selectedUPS.matricola || '-'}</p>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Ubicazione</p>
                                    <p className="text-gray-900">{selectedUPS.ubicazione || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Data Installazione</p>
                                    <p className="text-gray-900">
                                        {selectedUPS.data_installazione
                                            ? format(new Date(selectedUPS.data_installazione), 'dd MMMM yyyy', { locale: it })
                                            : '-'}
                                    </p>
                                </div>
                                {selectedUPS.note && (
                                    <div className="pt-2 border-t">
                                        <p className="text-sm font-medium text-gray-500">Note</p>
                                        <p className="text-gray-700 text-sm mt-1">{selectedUPS.note}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}